import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography, Pagination,
} from '@mui/material';
import { Add as AddIcon, Visibility } from '@mui/icons-material';
import { GoodsReceiptService } from '../../services/goodsReceipt.service';
import { GoodsReceipt, GoodsReceiptStatus } from '../../interfaces/procurement/goodsReceipt.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { formatFc, isForeignCurrency } from '../../utils/fcDocuments'; // G5a polish

const STATUS_COLORS: Record<GoodsReceiptStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'default',
  POSTED: 'success',
  REVERSED: 'error',
};

export const GoodsReceiptList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.GOODS_RECEIPT_MANAGE); // create is GOODS_RECEIPT_MANAGE server-side
  const [grns, setGrns] = useState<GoodsReceipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });

  const fetchGRNs = async (page = 0) => {
    try {
      setLoading(true);
      const response = await GoodsReceiptService.listByInstitution(page, 10);
      setGrns(response.content);
      setPageInfo({ currentPage: response.number, totalPages: response.totalPages, totalElements: response.totalElements });
    } catch (e) {
      toast.showApiError(e, 'Failed to load goods receipts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGRNs(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    fetchGRNs(page - 1);
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="store.grns.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Goods Receipts</Typography>
        {canManage && (
          <Button data-help="store.grns.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/procurement/goods-receipts/new')}>
            New Goods Receipt
          </Button>
        )}
      </Box>

      <Typography variant="body2" sx={{ mb: 1 }}>Total: {pageInfo.totalElements}</Typography>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper} data-help-section="store.grns.table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-help="store.grns.col.number">GRN Number</TableCell>
                <TableCell data-help="store.grns.col.po">PO Number</TableCell>
                <TableCell data-help="store.grns.col.date">Receipt Date</TableCell>
                <TableCell data-help="store.grns.col.status">Status</TableCell>
                <TableCell data-help="store.grns.col.value">Total Value</TableCell>
                <TableCell data-help="store.grns.col.actions">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grns.map(grn => (
                <TableRow key={grn.id}>
                  <TableCell>{grn.grnNumber}</TableCell>
                  <TableCell>{grn.purchaseOrderNumber ?? '—'}</TableCell>
                  <TableCell>{formatDate(grn.receiptDate)}</TableCell>
                  <TableCell>
                    <Chip label={humanizeEnum(grn.status)} color={STATUS_COLORS[grn.status]} size="small" />
                  </TableCell>
                  <TableCell>{formatMoney(grn.totalValue)}
                    {isForeignCurrency(grn.currencyCode) && grn.foreignTotalValue != null && (
                      <Typography variant="caption" display="block" color="text.secondary" data-testid="grn-foreign-total">{formatFc(grn.currencyCode!, grn.foreignTotalValue)}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View" arrow>
                      <IconButton
                        data-help="store.grns.action.view"
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/procurement/goods-receipts/${grn.id}`)}
                        aria-label="View"
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pageInfo.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pageInfo.totalPages}
            page={pageInfo.currentPage + 1}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};

export default GoodsReceiptList;
